// src/pages/evaluation-system/CreditFlowReduced.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Select from "react-select";
import moment from "moment";

import "../../EvaluationCreditFlow.css";


import Header from "../../components/Header";
import Footer from "../../components/Footer";
import ScrollToTop from "../../components/ScrollTop";
import PreloadFetch from "../../components/PreloadFetch";
import { useAuth } from "../../provider/authProvider";
import { useNavigate } from "react-router-dom";
import {
    handleAddressInput,
    handleCustomInput,
    handleDniInput,
    handleEmailInput,
    handleFullNameInput,
    handleImeiInput,
    handleLastNameInput,
    handleNumberInput,
    handlePhoneInput,
} from "../../utils/inputUtils";

const API = process.env.REACT_APP_API;
const FINANCIAL_ENDPOINT = process.env.REACT_APP_FINANCIAL_DATA;
const DNI_API_URL = "https://apiperu.dev/api/dni";
const DNI_API_BEARER = process.env.REACT_APP_APIPERU_TOKEN;

const axiosDni = axios.create({
    baseURL: DNI_API_URL,
    headers: {
        Authorization: `Bearer ${DNI_API_BEARER || ""}`,
        "Content-Type": "application/json",
        Accept: "application/json",
    },
});

const toNameCase = (input) => {
    const s = String(input ?? "").trim();
    if (!s) return "";
    return s
        .replace(/\s+/g, " ")
        .toLowerCase()
        .split(" ")
        .map((word) => word ? word.charAt(0).toUpperCase() + word.slice(1) : "")
        .join(" ");
};

const num = (v, def = 0) => {
    const n = Number(String(v ?? "").replace(/,/g, "."));
    return Number.isFinite(n) ? n : def;
};
const money = (v) => num(v).toFixed(2);
const onlyDigits = (v = "", max = 99) => String(v).replace(/\D/g, "").slice(0, max);
const toBase64 = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

const STEPS = [
    { id: 1, title: "Cotización", text: "Equipo y condiciones" },
    { id: 2, title: "Identificación", text: "Cliente y documentos" },
    { id: 3, title: "Perfil", text: "Domicilio y trabajo" },
    { id: 4, title: "Aprobación", text: "Preaprobación" },
    { id: 5, title: "Entrega", text: "QR y Firma" },
];

const livingPlaceOptions = [
    { value: "Casa Propia", label: "Casa Propia" },
    { value: "Alquilado", label: "Alquilado" },
];
const departmentOptions = ["Ucayali", "Lima Metropolitana", "San Martin", "Loreto"].map((v) => ({ value: v, label: v }));
const cityOptions = ["Pucallpa", "Lima", "Tarapoto", "Iquitos"].map((v) => ({ value: v, label: v }));
const jobTypeOptions = ["Publico", "Privado", "Independiente"].map((v) => ({ value: v, label: v }));
const civilStatusOptions = ["Unido", "Casado", "Soltero", "Divorciado", "Viudo"].map((v) => ({ value: v, label: v }));
const frequencyOptions = ["Diario", "Semanal", "Quincenal", "Mensual"].map((v) => ({ value: v, label: v }));

const ACTIVE_CREDIT_MESSAGE = "El cliente ya cuenta con un crédito activo en MovilesPay y se encuentra en periodo de pago. No puede solicitar un nuevo crédito hasta finalizar el crédito actual.";
const MAX_PHONE_PRICE = 2500;
const MAX_PHONE_PRICE_MESSAGE = "Solo se puede financiar equipos con precio máximo de S/ 2,500.00.";


const ecSelectStyles = {
    control: (base, state) => ({
        ...base,
        width: "100%",
        minHeight: 50,
        height: 50,
        borderRadius: 12,
        borderColor: state.isFocused ? "#111827" : "#d1d5db",
        boxShadow: state.isFocused ? "0 0 0 3px rgba(17,24,39,.08)" : "none",
        backgroundColor: "#fff",
        cursor: "pointer",
        alignItems: "center",
        "&:hover": {
            borderColor: state.isFocused ? "#111827" : "#d1d5db",
        },
    }),
    valueContainer: (base) => ({
        ...base,
        height: 48,
        minHeight: 48,
        padding: "0 14px",
        display: "flex",
        alignItems: "center",
        overflow: "hidden",
    }),
    placeholder: (base) => ({
        ...base,
        margin: 0,
        color: "#9ca3af",
        fontSize: 14,
        lineHeight: "20px",
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
    }),
    singleValue: (base) => ({
        ...base,
        margin: 0,
        color: "#111827",
        fontSize: 14,
        lineHeight: "20px",
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        maxWidth: "calc(100% - 8px)",
    }),
    input: (base) => ({
        ...base,
        margin: 0,
        padding: 0,
        color: "#111827",
        lineHeight: "20px",
    }),
    inputContainer: (base) => ({
        ...base,
        margin: 0,
        padding: 0,
    }),
    indicatorsContainer: (base) => ({
        ...base,
        height: 48,
        minHeight: 48,
        alignItems: "center",
    }),
    dropdownIndicator: (base) => ({
        ...base,
        padding: "0 12px",
        height: 48,
        display: "flex",
        alignItems: "center",
        color: "#6b7280",
    }),
    clearIndicator: (base) => ({
        ...base,
        padding: "0 8px",
        height: 48,
        display: "flex",
        alignItems: "center",
    }),
    indicatorSeparator: () => ({
        display: "none",
    }),
    menu: (base) => ({
        ...base,
        zIndex: 99999,
        borderRadius: 12,
        overflow: "hidden",
        boxShadow: "0 18px 35px rgba(15,23,42,.16)",
    }),
    menuPortal: (base) => ({
        ...base,
        zIndex: 99999,
    }),
    menuList: (base) => ({
        ...base,
        padding: 0,
        maxHeight: 260,
    }),
    option: (base, state) => ({
        ...base,
        minHeight: 44,
        padding: "12px 14px",
        fontSize: 14,
        cursor: "pointer",
        backgroundColor: state.isSelected ? "#111827" : state.isFocused ? "#f3f4f6" : "#fff",
        color: state.isSelected ? "#fff" : "#111827",
    }),
};

const ProfessionalSelect = React.memo(function ProfessionalSelect(props) {
    return (
        <Select
            {...props}
            className="ecSelect"
            classNamePrefix="ecSelect"
            styles={ecSelectStyles}
            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
            menuPosition="fixed"
            menuPlacement="auto"
            isSearchable
            autoComplete="new-password"
            inputId={props.inputId || `cf_select_${props.name || "field"}`}
            name={`cf_select_${props.name || "field"}`}
            blurInputOnSelect
        />
    );
});


const CREDIT_FLOW_STORAGE_PREFIX = "creditFlowReduced";
const CREDIT_FLOW_FORM_KEY = `${CREDIT_FLOW_STORAGE_PREFIX}:form`;
const CREDIT_FLOW_STEP_KEY = `${CREDIT_FLOW_STORAGE_PREFIX}:step`;
const CREDIT_FLOW_TYC_KEY = `${CREDIT_FLOW_STORAGE_PREFIX}:tyc`;
const CREDIT_FLOW_META_KEY = `${CREDIT_FLOW_STORAGE_PREFIX}:creditMeta`;
const CREDIT_FLOW_ENROLL_KEY = `${CREDIT_FLOW_STORAGE_PREFIX}:enroll`;
const CREDIT_FLOW_DOCUMENTS_KEY = `${CREDIT_FLOW_STORAGE_PREFIX}:documents`;
const CREDIT_FLOW_DEVICE_CONFIGURED_KEY = `${CREDIT_FLOW_STORAGE_PREFIX}:deviceConfigured`;
const CREDIT_FLOW_SIGNATURE_SMS_SENT_KEY = `${CREDIT_FLOW_STORAGE_PREFIX}:signatureSmsSent`;
const CREDIT_FLOW_FINISHED_KEY = `${CREDIT_FLOW_STORAGE_PREFIX}:finished`;

const loadJsonStorage = (key, fallback = null) => {
    try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : fallback;
    } catch (_) {
        return fallback;
    }
};

const FORM_STORAGE_KEYS = [
    "phoneName", "phonePrice", "phoneBrand", "phoneModel", "creditRecord", "initialPercent",
    "initialPrice", "paymentFrequency", "paymentPeriod", "numFee", "fee", "amountFinance",
    "totalAmountFinance", "daysPayment", "daysPaymentDays", "tcea", "loanType", "fullName",
    "lastName", "dni", "phoneNumber", "email", "dniFront", "dniBack", "clientVideo", "validationToken", "validationStatus",
    "livingPlace", "department", "city", "location", "locationNumber", "jobType", "jobName",
    "jobDirec", "civilStatus", "creditCard", "dependents", "monthlyIncome", "monthlyExpenses"
];

const saveFormToLocalStorage = (form, currentStep, currentTyc) => {
    try {
        localStorage.setItem(CREDIT_FLOW_FORM_KEY, JSON.stringify(form || {}));
        if (currentStep !== undefined) localStorage.setItem(CREDIT_FLOW_STEP_KEY, String(currentStep));
        if (currentTyc !== undefined) localStorage.setItem(CREDIT_FLOW_TYC_KEY, currentTyc ? "1" : "0");

        // Compatibilidad con tu flujo anterior: conserva las claves que ya usaba add-credit / Nuovo.
        FORM_STORAGE_KEYS.forEach((key) => {
            const value = form?.[key];
            if (value === undefined || value === null) return;
            localStorage.setItem(key, typeof value === "string" ? value : JSON.stringify(value));
        });
    } catch (_) {}
};

const loadCreditFlowDraft = () => loadJsonStorage(CREDIT_FLOW_FORM_KEY, null);

const clearCreditFlowStorage = () => {
    try {
        [
            CREDIT_FLOW_FORM_KEY,
            CREDIT_FLOW_STEP_KEY,
            CREDIT_FLOW_TYC_KEY,
            CREDIT_FLOW_META_KEY,
            CREDIT_FLOW_ENROLL_KEY,
            CREDIT_FLOW_DOCUMENTS_KEY,
            CREDIT_FLOW_DEVICE_CONFIGURED_KEY,
            CREDIT_FLOW_SIGNATURE_SMS_SENT_KEY,
            CREDIT_FLOW_FINISHED_KEY,
        ].forEach((key) => localStorage.removeItem(key));
        FORM_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
        ["initialPriceArr", "initialPricePosition", "contractDate", "tycSignContract", "idEnrrol", "dniEnroll", "imei", "duplicate", "servicePay", "linkNumber", "verifyLocation", "creditUploadName", "completed"].forEach((key) => localStorage.removeItem(key));
    } catch (_) {}
};

const antiAutofillAttrs = (name = "field") => ({
    name: `cf_${name}`,
    autoComplete: "new-password",
    autoCorrect: "off",
    autoCapitalize: "off",
    spellCheck: false,
});

const sanitizeDecimalInput = (value = "") =>
    String(value)
        .replace(/,/g, ".")
        .replace(/[^0-9.]/g, "")
        .replace(/(\..*)\./g, "$1")
        .slice(0, 12);

const applyInputValidation = (e, validation, max) => {
    switch (validation) {
        case "dni":
            handleDniInput(e);
            break;
        case "phone":
            handlePhoneInput(e);
            break;
        case "email":
            handleEmailInput(e);
            break;
        case "fullName":
            handleFullNameInput(e);
            break;
        case "lastName":
            handleLastNameInput(e);
            break;
        case "address":
            handleAddressInput(e);
            break;
        case "imei":
            handleImeiInput(e);
            break;
        case "number":
            handleNumberInput(e, max || 99);
            break;
        case "custom":
            handleCustomInput(e, max || 60);
            break;
        case "decimal":
            e.target.value = sanitizeDecimalInput(e.target.value);
            break;
        default:
            if (max) e.target.value = String(e.target.value || "").slice(0, max);
            break;
    }
};

const Field = React.memo(function Field({
    name,
    label,
    type = "text",
    max,
    placeholder,
    value,
    error,
    onUpdate,
    validation = "custom",
    readOnly = false,
    disabled = false,
}) {
    const isNumeric = ["dni", "phone", "number", "imei", "decimal"].includes(validation);

    const handleChange = (e) => {
        applyInputValidation(e, validation, max);
        onUpdate(name, e.target.value);
    };

    return (
        <label className="ecField">
            <span>{label}</span>
            <input
                {...antiAutofillAttrs(name)}
                type={type}
                value={value || ""}
                placeholder={placeholder || label}
                inputMode={validation === "decimal" ? "decimal" : isNumeric ? "numeric" : undefined}
                maxLength={max}
                onChange={handleChange}
                readOnly={readOnly}
                disabled={disabled}
            />
            {error && <small>{error}</small>}
        </label>
    );
});

const CreditCardRadio = React.memo(function CreditCardRadio({ value, error, onUpdate }) {
    return (
        <div className={`ecField ecRadioField ${error ? "input-error" : ""}`}>
            <span>Tarjeta de Crédito</span>
            <div className="ecRadioGroup" id="creditCardWrapper">
                <div className="ecRadioOption">
                    <input
                        autoComplete="off"
                        type="radio"
                        value="SI"
                        className="selectForm formItem"
                        onChange={(e) => onUpdate("creditCard", e.target.value)}
                        checked={value === "SI"}
                        name="tdc"
                        id="tdc1"
                    />
                    <label htmlFor="tdc1">
                        <strong>SI</strong>
                        <small>Cuenta con tarjeta</small>
                    </label>
                </div>

                <div className="ecRadioOption">
                    <input
                        autoComplete="off"
                        type="radio"
                        value="NO"
                        onChange={(e) => onUpdate("creditCard", e.target.value)}
                        checked={value === "NO"}
                        className="selectForm formItem"
                        name="tdc"
                        id="tdc2"
                    />
                    <label htmlFor="tdc2">
                        <strong>NO</strong>
                        <small>No cuenta con tarjeta</small>
                    </label>
                </div>
            </div>
            {error && <small>{error}</small>}
        </div>
    );
});

const DEFAULT_FORM = {
    phoneName: "",
    phonePrice: "",
    phoneBrand: "",
    phoneModel: "",
    creditRecord: "",
    initialPercent: "",
    initialPrice: "",
    paymentFrequency: "",
    paymentPeriod: -1,
    numFee: "",
    fee: "",
    amountFinance: "",
    totalAmountFinance: "",
    daysPayment: "[]",
    daysPaymentDays: "[]",
    tcea: "",
    loanType: "MP",

    fullName: "",
    lastName: "",
    dni: "",
    phoneNumber: "",
    email: "",
    dniFront: "",
    dniBack: "",
    clientVideo: "",
    validationToken: "",
    validationStatus: "pending",

    livingPlace: "",
    department: "",
    city: "",
    location: "",
    locationNumber: "",
    jobType: "",
    jobName: "",
    jobDirec: "",
    civilStatus: "",
    creditCard: "",
    dependents: "",
    monthlyIncome: "",
    monthlyExpenses: "",
};

export default function CreditFlowReduced() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [step, setStep] = useState(() => Number(localStorage.getItem(CREDIT_FLOW_STEP_KEY)) || 1);
    const [loading, setLoading] = useState(false);
    const [dniLookupLoading, setDniLookupLoading] = useState(false);
    const [dniCreditValidation, setDniCreditValidation] = useState({ loading: false, blocked: false, message: "", credit: null });
    const [phones, setPhones] = useState([]);
    const [phonesLoadError, setPhonesLoadError] = useState("");
    const [errors, setErrors] = useState({});
    const [tyc, setTyc] = useState(() => localStorage.getItem(CREDIT_FLOW_TYC_KEY) === "1");
    const [creditMeta, setCreditMeta] = useState(() => loadJsonStorage(CREDIT_FLOW_META_KEY, null));
    const [enroll, setEnroll] = useState(() => ({
        imei: "",
        qrCode: "",
        completed: false,
        message: "",
        ...(loadJsonStorage(CREDIT_FLOW_ENROLL_KEY, {}) || {}),
    }));
    const [finalizingEnroll, setFinalizingEnroll] = useState(false);
    const [validationLoading, setValidationLoading] = useState(false);
    const [validationQr, setValidationQr] = useState({ token: "", url: "", qrUrl: "", status: "pending", message: "" });
    const [deviceConfigured, setDeviceConfigured] = useState(() => localStorage.getItem(CREDIT_FLOW_DEVICE_CONFIGURED_KEY) === "1");
    const [signatureCode, setSignatureCode] = useState("");
    const [signatureLoading, setSignatureLoading] = useState(false);
    const [signatureSmsSent, setSignatureSmsSent] = useState(() => localStorage.getItem(CREDIT_FLOW_SIGNATURE_SMS_SENT_KEY) === "1");
    const [signatureVerified, setSignatureVerified] = useState(() => localStorage.getItem(CREDIT_FLOW_FINISHED_KEY) === "1" || localStorage.getItem("completed") === "1");
    const [documents, setDocuments] = useState(() => loadJsonStorage(CREDIT_FLOW_DOCUMENTS_KEY, { legalContract: "", anexo2: "", anexo3: "", financingCertificate: "" }));
    const [flowMessage, setFlowMessage] = useState("");
    const lastDniLookupRef = useRef("");
    const dniLookupRequestRef = useRef(0);
    const dniCreditValidationRequestRef = useRef(0);
    const financialRequestRef = useRef(0);

    const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });


    const buildUploadFileUrl = (uploadName, fileName) => {
        if (!uploadName || !fileName) return "";
        const base = String(API || "").replace(/\/$/, "");
        return `${base}/data-files/${encodeURIComponent(uploadName)}/${encodeURIComponent(fileName)}`;
    };

    const financingPdfUrl = buildUploadFileUrl(
        creditMeta?.uploadName,
        creditMeta?.financingCertificate || "constancia-financiamiento.pdf"
    );


    const [form, setForm] = useState(() => ({ ...DEFAULT_FORM, ...(loadCreditFlowDraft() || {}) }));
    const formRef = useRef(form);

    useEffect(() => {
        formRef.current = form;
    }, [form]);


    const update = useCallback((name, value) => {
        setForm((f) => ({ ...f, [name]: value }));
        setErrors((e) => ({ ...e, [name]: undefined }));
    }, []);

    const updateMany = useCallback((values = {}) => {
        setForm((f) => ({ ...f, ...values }));
        setErrors((e) => {
            const next = { ...e };
            Object.keys(values).forEach((key) => {
                next[key] = undefined;
            });
            return next;
        });
    }, []);

    const getPublicBaseUrl = useCallback(() => {
        const candidate = process.env.REACT_APP_PUBLIC_FRONT_URL || process.env.REACT_APP_FRONT_URL || (typeof window !== "undefined" ? window.location.origin : "");
        if (!candidate) return "";
        try {
            return new URL(candidate, window.location.origin).origin;
        } catch (_) {
            return String(candidate).replace(/\/$/, "");
        }
    }, []);

    const buildValidationUrl = useCallback((validationToken, dni, phone) => {
        const base = String(getPublicBaseUrl() || "").replace(/\/$/, "");
        const params = new URLSearchParams({
            publicValidation: "1",
            validationToken: String(validationToken || "").trim(),
            dni: onlyDigits(dni, 8),
            phone: onlyDigits(phone, 9),
        });

        // URL pública final del QR:
        // https://tudominio.com/user-validation?publicValidation=1&validationToken=...&dni=...&phone=...
        // No depende de sesión del cliente; solo usa token + DNI + celular.
        return `${base}/user-validation?${params.toString()}`;
    }, [getPublicBaseUrl]);

    const refreshClientValidationStatus = useCallback(async (token = form.validationToken) => {
        if (!token) return null;
        try {
            setValidationLoading(true);
            const { data } = await axios.get(`${API}/credit-validation/session/${encodeURIComponent(token)}`, {
                headers: authHeaders(),
            });

            // No confiamos únicamente en cv_status porque una sesión puede quedar
            // marcada como completed por error o por datos antiguos.
            // Solo se considera completada si el backend confirma evidenceComplete === true.
            const evidenceComplete = data?.data?.evidenceComplete === true;
            const status = evidenceComplete ? "completed" : "pending";

            update("validationStatus", status);
            setValidationQr((old) => ({
                ...old,
                token,
                status,
                message: evidenceComplete ? "Validación completada correctamente." : "Validación pendiente desde el celular.",
            }));
            return status;
        } catch (err) {
            setValidationQr((old) => ({ ...old, message: "No se pudo consultar la validación del cliente." }));
            return null;
        } finally {
            setValidationLoading(false);
        }
    }, [API, form.validationToken, update]);

    const createClientValidationQr = useCallback(async () => {
        const dni = String(form.dni || "").trim();
        const phone = String(form.phoneNumber || "").trim();
        if (!/^\d{8}$/.test(dni)) {
            setErrors((e) => ({ ...e, dni: "Ingrese un DNI válido antes de generar el QR." }));
            return;
        }
        if (!/^9\d{8}$/.test(phone)) {
            setErrors((e) => ({ ...e, phoneNumber: "Ingrese un celular válido antes de generar el QR." }));
            return;
        }

        try {
            setValidationLoading(true);

            // Limpieza preventiva: evita que el asesor siga viendo o escaneando
            // un QR anterior que ya pudo haber sido utilizado.
            update("validationToken", "");
            update("validationStatus", "pending");
            setValidationQr({
                token: "",
                url: "",
                qrUrl: "",
                status: "pending",
                message: "Generando nuevo QR de validación...",
            });

            const frontUrl = getPublicBaseUrl();
            const { data } = await axios.post(`${API}/credit-validation/session`, {
                dni,
                phoneNumber: phone,
                fullName: `${form.fullName || ""} ${form.lastName || ""}`.trim(),
                frontUrl,
            }, { headers: { ...authHeaders(), "Content-Type": "application/json" } });

            if (data?.msg !== "success" || !data?.data?.token) throw new Error("No se pudo generar la validación.");
            if (!data?.data?.qrCode) throw new Error("El backend no devolvió el QR en base64. Verifique que esté instalado el paquete qrcode.");

            const token = data.data.validationToken || data.data.token;
            const url = data.data.url || buildValidationUrl(token, dni, phone);
            const qrUrl = data.data.qrCode; // data:image/png;base64,...

            update("validationToken", token);
            update("validationStatus", data.data.status || "pending");
            setValidationQr({
                token,
                url,
                qrUrl,
                status: data.data.status || "pending",
                message: "QR generado. Escanee el QR con el celular para completar la validación facial del cliente.",
            });
        } catch (err) {
            alert(err?.response?.data?.msg || err?.message || "No se pudo generar el QR de validación.");
        } finally {
            setValidationLoading(false);
        }
    }, [API, buildValidationUrl, form.dni, form.phoneNumber, form.fullName, form.lastName, update]);


    useEffect(() => {
        saveFormToLocalStorage(form, step, tyc);
    }, [form, step, tyc]);

    useEffect(() => {
        try {
            if (creditMeta) localStorage.setItem(CREDIT_FLOW_META_KEY, JSON.stringify(creditMeta));
        } catch (_) {}
    }, [creditMeta]);

    useEffect(() => {
        try {
            localStorage.setItem(CREDIT_FLOW_ENROLL_KEY, JSON.stringify(enroll || {}));
        } catch (_) {}
    }, [enroll]);

    useEffect(() => {
        try {
            localStorage.setItem(CREDIT_FLOW_DEVICE_CONFIGURED_KEY, deviceConfigured ? "1" : "0");
        } catch (_) {}
    }, [deviceConfigured]);

    useEffect(() => {
        try {
            localStorage.setItem(CREDIT_FLOW_SIGNATURE_SMS_SENT_KEY, signatureSmsSent ? "1" : "0");
        } catch (_) {}
    }, [signatureSmsSent]);

    useEffect(() => {
        try {
            localStorage.setItem(CREDIT_FLOW_DOCUMENTS_KEY, JSON.stringify(documents || {}));
        } catch (_) {}
    }, [documents]);

    useEffect(() => {
        if (!signatureVerified) return;
        try {
            localStorage.setItem(CREDIT_FLOW_FINISHED_KEY, "1");
            localStorage.setItem("completed", "1");
            localStorage.setItem(CREDIT_FLOW_STEP_KEY, "5");
        } catch (_) {}
        setStep(5);
    }, [signatureVerified]);

    const getOptionByValue = useCallback((options, value) => {
        return (options || []).find((option) => String(option.value) === String(value)) || null;
    }, []);

    const goOutOfCreditFlow = useCallback(() => {
        clearCreditFlowStorage();
        navigate("/evaluation-system", { replace: true });
    }, [navigate]);


    const validateActiveCreditByDni = useCallback(async (dniValue, requestId) => {
        const dni = String(dniValue || "").trim();
        if (!/^\d{8}$/.test(dni)) {
            setDniCreditValidation({ loading: false, blocked: false, message: "", credit: null });
            return { blocked: false };
        }

        const currentRequestId = requestId || ++dniCreditValidationRequestRef.current;

        try {
            setDniCreditValidation((old) => ({ ...old, loading: true, message: "Validando crédito activo en MovilesPay..." }));
            const { data } = await axios.post(`${API}/credit/validate-active-by-dni`, { dni }, {
                headers: { ...authHeaders(), "Content-Type": "application/json" },
            });

            if (currentRequestId !== dniCreditValidationRequestRef.current) return { blocked: false, stale: true };

            const blocked = Boolean(data?.blocked);
            const message = blocked ? (data?.msg || ACTIVE_CREDIT_MESSAGE) : "";
            setDniCreditValidation({ loading: false, blocked, message, credit: data?.credit || null });

            if (blocked) {
                setErrors((current) => ({ ...current, dni: message }));
                setForm((current) => {
                    if (String(current.dni || "").trim() !== dni) return current;
                    return {
                        ...current,
                        fullName: "",
                        lastName: "",
                        validationToken: "",
                        validationStatus: "pending",
                    };
                });
                setValidationQr({ token: "", url: "", qrUrl: "", status: "pending", message: "" });
                setDniCreditValidation({ loading: false, blocked: false, message: "", credit: null });
                lastDniLookupRef.current = "";
            }

            return { blocked, credit: data?.credit || null };
        } catch (err) {
            if (currentRequestId === dniCreditValidationRequestRef.current) {
                const msg = err?.response?.data?.msg || "No se pudo validar si el cliente tiene crédito activo. Intente nuevamente.";
                setDniCreditValidation({ loading: false, blocked: true, message: msg, credit: null });
                setErrors((current) => ({ ...current, dni: msg }));
            }
            return { blocked: true, error: true };
        } finally {
            if (currentRequestId === dniCreditValidationRequestRef.current) {
                setDniCreditValidation((old) => ({ ...old, loading: false }));
            }
        }
    }, [API]);

    const lookupDni = useCallback(async (dniValue, requestId) => {
        const dni = String(dniValue || "").trim();
        if (!/^\d{8}$/.test(dni) || !DNI_API_BEARER) return;

        const currentRequestId = requestId || ++dniLookupRequestRef.current;

        try {
            setDniLookupLoading(true);
            const res = await axiosDni.post("/", { dni });

            // Evita que una respuesta antigua sobreescriba los datos cuando el asesor cambió rápido de DNI.
            if (currentRequestId !== dniLookupRequestRef.current) return;

            const data = res?.data?.data || res?.data || {};
            const nombres = data?.nombres || data?.name || "";
            const apPat = data?.apellido_paterno || data?.apellidoPaterno || data?.paterno || "";
            const apMat = data?.apellido_materno || data?.apellidoMaterno || data?.materno || "";
            const fullName = nombres ? toNameCase(nombres) : "";
            const lastName = [apPat, apMat].filter(Boolean).map(toNameCase).join(" ");

            setForm((current) => {
                // Si el DNI cambió mientras se consultaba, no escribas datos de otro cliente.
                if (String(current.dni || "").trim() !== dni) return current;
                return {
                    ...current,
                    fullName,
                    lastName,
                };
            });

            lastDniLookupRef.current = dni;
            setErrors((current) => ({ ...current, fullName: undefined, lastName: undefined }));
        } catch (err) {
            if (currentRequestId === dniLookupRequestRef.current) {
                lastDniLookupRef.current = "";
            }
            console.warn("No se pudo autocompletar DNI:", err?.response?.data || err?.message);
        } finally {
            if (currentRequestId === dniLookupRequestRef.current) {
                setDniLookupLoading(false);
            }
        }
    }, []);

    const handleDniChange = useCallback((e) => {
        applyInputValidation(e, "dni", 8);
        const nextDni = e.target.value;

        dniLookupRequestRef.current += 1;
        dniCreditValidationRequestRef.current += 1;
        lastDniLookupRef.current = "";

        setForm((current) => {
            const dniChanged = String(current.dni || "") !== String(nextDni || "");
            return {
                ...current,
                dni: nextDni,
                ...(dniChanged
                    ? {
                        fullName: "",
                        lastName: "",
                        validationToken: "",
                        validationStatus: "pending",
                    }
                    : {}),
            };
        });

        setValidationQr({ token: "", url: "", qrUrl: "", status: "pending", message: "" });
        setErrors((current) => ({
            ...current,
            dni: undefined,
            fullName: undefined,
            lastName: undefined,
            validation: undefined,
        }));
    }, []);

    useEffect(() => {
        const dni = String(form.dni || "").trim();

        if (!/^\d{8}$/.test(dni)) {
            lastDniLookupRef.current = "";
            setDniLookupLoading(false);
            return undefined;
        }

        if (dni === lastDniLookupRef.current) return undefined;

        const requestId = dniLookupRequestRef.current + 1;
        dniLookupRequestRef.current = requestId;
        dniCreditValidationRequestRef.current = requestId;

        const timer = setTimeout(async () => {
            const availability = await validateActiveCreditByDni(dni, requestId);
            if (!availability?.blocked) lookupDni(dni, requestId);
        }, 350);
        return () => clearTimeout(timer);
    }, [form.dni, lookupDni, validateActiveCreditByDni]);



    useEffect(() => {
        if (!form.validationToken) return;
        const dni = String(form.dni || "").trim();
        const phone = String(form.phoneNumber || "").trim();
        const url = /^\d{8}$/.test(dni) && /^9\d{8}$/.test(phone) ? buildValidationUrl(form.validationToken, dni, phone) : "";
        setValidationQr((old) => ({
            ...old,
            token: form.validationToken,
            url,
            // El QR ya viene como base64 desde el backend. No reconstruimos una ruta /qr para evitar problemas de HTTPS, CORS o Authorization.
            qrUrl: old.qrUrl,
            status: form.validationStatus || old.status || "pending",
        }));
    }, [API, buildValidationUrl, form.validationToken, form.validationStatus, form.dni, form.phoneNumber]);

    useEffect(() => {
        return () => {
            const path = String(window.location?.pathname || "").toLowerCase();
            const stillInCreditFlow = path.includes("credit-flow") || path.includes("creditflow");
            if (!stillInCreditFlow) clearCreditFlowStorage();
        };
    }, []);

    useEffect(() => {
        let mounted = true;

        const loadPhones = async () => {
            try {
                setLoading(true);
                setPhonesLoadError("");

                // Esta ruta resuelve la sede en backend usando la sesión/token.
                // No enviamos sedeId desde el navegador para evitar que un asesor
                // pueda consultar stock de otra sede manipulando la petición.
                const { data } = await axios.get(`${API}/credit-flow/available-phones`, {
                    headers: authHeaders(),
                });

                const options = (data?.data || [])
                    .filter((p) => num(p.price) > 0 && num(p.stockAvailable) > 0)
                    .map((p) => ({
                        value: p.value,
                        label: p.label,
                        price: money(p.price),
                        brand: p.brand || p.phoneBrand || "",
                        model: p.model || p.phoneModel || p.value,
                        productId: p.productId || null,
                        stockAvailable: num(p.stockAvailable),
                        sedeId: p.sedeId || null,
                        sedeName: p.sedeName || "",
                    }));

                if (!mounted) return;
                setPhones(options);

                if (!options.length) {
                    setPhonesLoadError(data?.message || "No hay equipos disponibles en la sede asignada al usuario.");
                }
            } catch (err) {
                if (!mounted) return;
                setPhones([]);
                setPhonesLoadError(
                    err?.response?.data?.msg ||
                    err?.response?.data?.error ||
                    "No se pudo consultar el stock disponible de tu sede."
                );
            } finally {
                if (mounted) setLoading(false);
            }
        };

        loadPhones();
        return () => { mounted = false; };
    }, [API]);

    const initials = useMemo(() => {
        const score = num(form.creditRecord, -1);
        const price = num(form.phonePrice);
        const mk = (pct) => ({ pct, price: money(price * (pct / 100)) });
        if (!price || score < 0) return [];
        if (price > MAX_PHONE_PRICE) return [];
        if (score === 0) return [mk(50)];
        if (score > 0 && score < 499) return [];
        if (score <= 500) return [50, 60, 70, 80].map(mk);
        if (score <= 600) return [40, 45, 50, 60, 70, 80].map(mk);
        if (score <= 700) return [30, 35, 40, 45, 50, 60, 70, 80].map(mk);

        // Score 701 a más:
        // Se agregan iniciales bajas para clientes con mejor historial.
        // Primero se muestran 5%, 10% y 15%, luego las iniciales existentes.
        return [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 60, 70, 80].map(mk);
    }, [form.creditRecord, form.phonePrice]);

    const periods = useMemo(() => {
        if (form.paymentFrequency === "Diario") return ["30 días", "45 días"];
        if (form.paymentFrequency === "Semanal") return ["4 Semanas", "8 Semanas", "12 Semanas", "16 Semanas", "20 Semanas", "24 Semanas"];
        if (form.paymentFrequency === "Quincenal") return ["2 Quincenas", "4 Quincenas", "6 Quincenas", "8 Quincenas", "10 Quincenas", "12 Quincenas"];
        if (form.paymentFrequency === "Mensual") return ["1 Mes", "2 Meses", "3 Meses", "4 Meses", "5 Meses", "6 Meses"];
        return [];
    }, [form.paymentFrequency]);

    const clearFinancialResult = useCallback((extra = {}) => ({
        ...extra,
        numFee: "",
        fee: "",
        totalAmountFinance: "",
        daysPayment: "[]",
        daysPaymentDays: "[]",
        tcea: "",
    }), []);

    const selectPhone = (opt) => {
        const selectedPrice = money(opt?.price || 0);
        updateMany(clearFinancialResult({
            phoneName: opt?.value || "",
            phonePrice: opt?.price || "",
            phoneBrand: opt?.brand || "",
            phoneModel: opt?.model || opt?.value || "",
            initialPercent: "",
            initialPrice: "",
            amountFinance: "",
        }));

        if (num(selectedPrice) > MAX_PHONE_PRICE) {
            setErrors((current) => ({
                ...current,
                phonePrice: MAX_PHONE_PRICE_MESSAGE,
                initialPrice: undefined,
                fee: undefined,
            }));
        }
    };

    const handleQuoteUpdate = useCallback((name, value) => {
        if (name === "phonePrice") {
            // IMPORTANTE UX:
            // No convertir a money(value) mientras el asesor escribe.
            // Antes, al digitar "1" se transformaba inmediatamente en "1.00",
            // dejando el input controlado y dificultando escribir 1000, 1200, etc.
            const nextPrice = sanitizeDecimalInput(value);
            const priceNumber = num(nextPrice);
            const pct = num(form.initialPercent);

            if (priceNumber > MAX_PHONE_PRICE) {
                updateMany(clearFinancialResult({
                    phonePrice: nextPrice,
                    initialPercent: "",
                    initialPrice: "",
                    amountFinance: "",
                }));
                setErrors((current) => ({ ...current, phonePrice: MAX_PHONE_PRICE_MESSAGE }));
                return;
            }

            if (pct > 0 && priceNumber > 0) {
                const nextInitialPrice = money(priceNumber * (pct / 100));
                const nextAmountFinance = money(Math.max(0, priceNumber - num(nextInitialPrice)));
                updateMany(clearFinancialResult({
                    phonePrice: nextPrice,
                    initialPrice: nextInitialPrice,
                    amountFinance: nextAmountFinance,
                }));
                return;
            }

            updateMany(clearFinancialResult({
                phonePrice: nextPrice,
                initialPrice: "",
                amountFinance: "",
            }));
            return;
        }

        if (name === "creditRecord") {
            updateMany(clearFinancialResult({
                creditRecord: value,
                initialPercent: "",
                initialPrice: "",
                amountFinance: "",
            }));
            return;
        }

        update(name, value);
    }, [clearFinancialResult, form.initialPercent, update, updateMany]);

    const selectInitial = (it) => {
        if (num(form.phonePrice) > MAX_PHONE_PRICE) {
            setErrors((current) => ({ ...current, phonePrice: MAX_PHONE_PRICE_MESSAGE }));
            return;
        }

        updateMany(clearFinancialResult({
            initialPercent: String(it.pct),
            initialPrice: it.price,
            amountFinance: money(Math.max(0, num(form.phonePrice) - num(it.price))),
        }));
    };

    const selectFrequency = (opt) => {
        const nextFrequency = opt?.value || "";
        updateMany(clearFinancialResult({
            paymentFrequency: nextFrequency,
            paymentPeriod: form.paymentPeriod >= 0 ? form.paymentPeriod : -1,
        }));
    };

    const calculateFinancial = useCallback(async (sourceForm = null, options = {}) => {
        const currentForm = sourceForm || formRef.current;
        const silent = Boolean(options.silent);

        if (!FINANCIAL_ENDPOINT) {
            if (!silent) alert("Falta configurar REACT_APP_FINANCIAL_DATA.");
            return false;
        }

        const phonePriceNumber = num(currentForm.phonePrice);
        const amountFinance = money(currentForm.amountFinance);
        const paymentFrequency = currentForm.paymentFrequency;
        const paymentPeriod = Number(currentForm.paymentPeriod);

        if (phonePriceNumber > MAX_PHONE_PRICE) {
            setErrors((current) => ({ ...current, phonePrice: MAX_PHONE_PRICE_MESSAGE }));
            if (!silent) alert(MAX_PHONE_PRICE_MESSAGE);
            return false;
        }

        if (!num(amountFinance) || !paymentFrequency || paymentPeriod < 0) return false;

        const requestId = ++financialRequestRef.current;

        try {
            if (!silent) setLoading(true);
            const { data } = await axios.post(
                `${API}${FINANCIAL_ENDPOINT}`,
                {
                    amountFinance,
                    paymentFrequency,
                    paymentPeriod,
                    initialPercent: currentForm.initialPercent,
                    phonePrice: money(currentForm.phonePrice),
                    tierBaseAmount: money(currentForm.phonePrice),
                    initialPrice: money(currentForm.initialPrice),
                },
                { headers: { "Content-Type": "application/json" } }
            );

            if (requestId !== financialRequestRef.current) return false;
            if (data?.msg !== "exito") throw new Error("No calculado");

            updateMany({
                numFee: String(data?.data?.feeNum || ""),
                fee: money(data?.data?.feePrice),
                totalAmountFinance: money(data?.data?.totalAmount),
                daysPayment: JSON.stringify(data?.data?.objPayment || []),
                daysPaymentDays: JSON.stringify(data?.data?.objPaymentTwo || []),
                tcea: data?.data?.tcea || "",
            });
            return true;
        } catch (err) {
            if (!silent) alert(err?.response?.data?.error || err?.response?.data?.msg || "No se pudo calcular el financiamiento.");
            return false;
        } finally {
            if (!silent && requestId === financialRequestRef.current) setLoading(false);
        }
    }, [API, FINANCIAL_ENDPOINT, updateMany]);

    useEffect(() => {
        if (!form.amountFinance || !form.paymentFrequency || form.paymentPeriod < 0 || !form.initialPercent) return;

        const timer = setTimeout(() => {
            calculateFinancial(form, { silent: true });
        }, 250);

        return () => clearTimeout(timer);
    }, [form.amountFinance, form.paymentFrequency, form.paymentPeriod, form.initialPercent, calculateFinancial]);

    const validateStep = (n = step) => {
        const e = {};
        if (n === 1) {
            if (!form.phoneName) e.phoneName = "Seleccione el equipo.";
            if (!num(form.phonePrice)) e.phonePrice = "Ingrese precio válido.";
            if (num(form.phonePrice) > MAX_PHONE_PRICE) e.phonePrice = MAX_PHONE_PRICE_MESSAGE;
            if (!form.creditRecord) e.creditRecord = "Ingrese score.";
            if (initials.length === 0) e.creditRecord = "El cliente no accede a crédito según el score.";
            if (!form.initialPrice) e.initialPrice = "Seleccione inicial.";
            if (!form.paymentFrequency) e.paymentFrequency = "Seleccione frecuencia.";
            if (form.paymentPeriod < 0) e.paymentPeriod = "Seleccione período.";
            if (!form.fee || !form.numFee) e.fee = "Calcule el financiamiento.";
        }
        if (n === 2) {
            if (!/^\d{8}$/.test(form.dni)) e.dni = "DNI debe tener 8 dígitos.";
            if (dniCreditValidation.loading) e.dni = "Espere la validación del DNI.";
            if (dniCreditValidation.blocked) e.dni = dniCreditValidation.message || ACTIVE_CREDIT_MESSAGE;
            if (!form.fullName.trim()) e.fullName = "Ingrese nombres.";
            if (!form.lastName.trim()) e.lastName = "Ingrese apellidos.";
            if (!/^9\d{8}$/.test(form.phoneNumber)) e.phoneNumber = "Celular debe tener 9 dígitos e iniciar con 9.";
            if (!form.email.includes("@")) e.email = "Ingrese correo válido.";
            if (!form.validationToken) e.validation = "Genere el QR de validación del cliente.";
            if (form.validationStatus !== "completed") e.validation = "El cliente debe completar la validación por QR antes de continuar.";
        }
        if (n === 3) {
            ["livingPlace", "department", "city", "location", "locationNumber", "jobType", "jobName", "jobDirec", "civilStatus", "creditCard", "dependents", "monthlyIncome", "monthlyExpenses"].forEach((k) => {
                if (!String(form[k] || "").trim()) e[k] = "Campo obligatorio.";
            });
        }
        if (n === 4) {
            if (!tyc) e.tyc = "Debe aceptar términos y condiciones.";
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const next = async () => {
        if (step === 2) {
            const dniAvailability = await validateActiveCreditByDni(form.dni);
            if (dniAvailability?.blocked) {
                setErrors((current) => ({ ...current, dni: dniAvailability.message || dniCreditValidation.message || ACTIVE_CREDIT_MESSAGE }));
                return;
            }

            let status = form.validationStatus === "completed" || validationQr.status === "completed" ? "completed" : form.validationStatus;
            if (form.validationToken && status !== "completed") {
                status = await refreshClientValidationStatus(form.validationToken);
            }

            const e = {};
            if (!/^\d{8}$/.test(form.dni)) e.dni = "DNI debe tener 8 dígitos.";
            if (dniCreditValidation.blocked) e.dni = dniCreditValidation.message || ACTIVE_CREDIT_MESSAGE;
            if (!form.fullName.trim()) e.fullName = "Ingrese nombres.";
            if (!form.lastName.trim()) e.lastName = "Ingrese apellidos.";
            if (!/^9\d{8}$/.test(form.phoneNumber)) e.phoneNumber = "Celular debe tener 9 dígitos e iniciar con 9.";
            if (!form.email.includes("@")) e.email = "Ingrese correo válido.";
            if (!form.validationToken) e.validation = "Genere el QR de validación del cliente.";
            if (status !== "completed") e.validation = "El cliente debe completar la validación por QR antes de continuar.";
            setErrors(e);
            if (Object.keys(e).length > 0) return;
            update("validationStatus", "completed");
            saveFormToLocalStorage({ ...form, validationStatus: "completed" }, step, tyc);
            setStep(3);
            return;
        }

        if (!validateStep(step)) return;
        if (step === 1 && (!form.fee || !form.numFee)) {
            const ok = await calculateFinancial();
            if (!ok) return;
        }
        saveFormToLocalStorage(form, step, tyc);
        setStep((s) => Math.min(s + 1, 5));
    };

    const submitCredit = async () => {
        if (!validateStep(4)) return;
        saveFormToLocalStorage(form, step, tyc);
        try {
            setLoading(true);
            const payload = {
                ...form,
                tycSignContract: tyc ? "SI" : "NO",
                currentDate: moment().format("YYYY-MM-DD"),
                contractDate: moment().format("YYYY-MM-DD"),
                validationToken: form.validationToken,
            };
            const { data } = await axios.post(`${API}/add-credit`, payload, {
                headers: {
                    ...authHeaders(),
                    "Content-Type": "application/json",
                },
            });
            if (data?.msg !== "success") throw new Error(data?.msg || "error");

            const nextMeta = {
                linkNumber: data.linkNumer,
                dni: form.dni,
                uploadName: data.uploadName || "",
                financingCertificate: "",
                legalContract: "",
                anexo2: "",
                anexo3: "",
            };

            setCreditMeta(nextMeta);
            localStorage.setItem("dniEnroll", form.dni);
            localStorage.setItem("linkNumber", data.linkNumer);
            localStorage.setItem("creditUploadName", nextMeta.uploadName);
            setFlowMessage("Crédito guardado correctamente. Continúe con el IMEI, QR App de seguridad, configuración del equipo y firma SMS.");
            setStep(5);
        } catch (err) {
            alert("No se pudo registrar el crédito. Verifique sede, sesión y datos obligatorios.");
        } finally {
            setLoading(false);
        }
    };

    const registerDevice = async () => {
        const dni = creditMeta?.dni || form.dni;
        const linkNumber = creditMeta?.linkNumber || localStorage.getItem("linkNumber");
        const imei = onlyDigits(enroll.imei, 15);

        if (!/^\d{8}$/.test(dni)) {
            alert("DNI inválido para enrolamiento.");
            return;
        }
        if (!/^\d{15}$/.test(imei)) {
            alert("El IMEI debe tener 15 dígitos.");
            return;
        }

        try {
            setLoading(true);
            const { data } = await axios.post(
                `${API}/apin-system/register-device`,
                { getDni: dni, imeiData: imei, linkNumber },
                { headers: { ...authHeaders(), "Content-Type": "application/json" } }
            );

            if (data?.msg !== "exito") throw new Error(data?.data || "No se pudo registrar el equipo en App de seguridad.");
            setEnroll((e) => ({ ...e, imei, qrCode: data.codeDataQr || "", message: "Equipo registrado. Escanee el QR para completar la activación." }));
        } catch (err) {
            alert(err?.response?.data?.data || err?.message || "No se pudo registrar el equipo en App de seguridad.");
        } finally {
            setLoading(false);
        }
    };

    const completeEnroll = async () => {
        if (finalizingEnroll || enroll.completed) return;

        const dni = creditMeta?.dni || form.dni;
        const linkNumber = creditMeta?.linkNumber || localStorage.getItem("linkNumber");
        const imei = onlyDigits(enroll.imei, 15);

        if (!enroll.qrCode) {
            alert("Primero debe registrar el equipo y generar el QR.");
            return;
        }
        if (!/^\d{15}$/.test(imei)) {
            alert("El IMEI debe tener 15 dígitos.");
            return;
        }

        try {
            setFinalizingEnroll(true);
            setLoading(true);
            const { data } = await axios.post(
                `${API}/credit/complete-enroll`,
                { dni, imei, linkNumber },
                { headers: { ...authHeaders(), "Content-Type": "application/json" } }
            );

            if (data?.msg !== "success") throw new Error(data?.msg || "No se pudo finalizar el enrolamiento.");

            setCreditMeta((m) => ({
                ...(m || {}),
                linkNumber,
                dni,
                uploadName: data.uploadName || m?.uploadName || localStorage.getItem("creditUploadName") || "",
            }));
            setEnroll((e) => ({ ...e, completed: true, message: data.message || "Equipo enrolado correctamente. Configure el celular nuevo y luego envíe el SMS de firma digital." }));
            setFlowMessage("App de seguridad completado. Confirme que el celular nuevo quedó configurado antes de enviar el SMS de firma digital.");
        } catch (err) {
            alert(err?.response?.data?.msg || err?.message || "No se pudo finalizar la venta.");
        } finally {
            setFinalizingEnroll(false);
            setLoading(false);
        }
    };


    const sendSignatureSms = async () => {
        const dni = creditMeta?.dni || form.dni;
        const linkNumber = creditMeta?.linkNumber || localStorage.getItem("linkNumber");

        if (!enroll.completed) {
            alert("Primero debe completar el enrolamiento del App de seguridad.");
            return;
        }
        if (!deviceConfigured) {
            alert("Confirme que el celular nuevo ya fue configurado antes de enviar el SMS.");
            return;
        }

        try {
            setSignatureLoading(true);
            const { data } = await axios.post(`${API}/credit/get-sms-code`, { dni, linkNumber }, {
                headers: { ...authHeaders(), "Content-Type": "application/json" },
            });

            if (data?.msg !== "success") throw new Error(data?.msg || "No se pudo enviar el SMS.");
            setSignatureSmsSent(true);
            setFlowMessage("SMS enviado al celular del cliente. Ingrese el código para generar los documentos finales.");
        } catch (err) {
            alert(err?.response?.data?.msg || err?.message || "No se pudo enviar el SMS de firma digital.");
        } finally {
            setSignatureLoading(false);
        }
    };

    const verifySignatureSms = async () => {
        const dni = creditMeta?.dni || form.dni;
        const linkNumber = creditMeta?.linkNumber || localStorage.getItem("linkNumber");
        const smsCode = onlyDigits(signatureCode, 10);

        if (!smsCode) {
            alert("Ingrese el código SMS recibido por el cliente.");
            return;
        }

        try {
            setSignatureLoading(true);
            const { data } = await axios.post(`${API}/credit/verify-sms-code`, { dni, linkNumber, smsCode }, {
                headers: { ...authHeaders(), "Content-Type": "application/json" },
            });

            if (data?.msg !== "success") {
                if (data?.data === 3) throw new Error("El código SMS no coincide.");
                throw new Error(data?.msg || "No se pudo validar la firma digital.");
            }

            const docs = data.documents || {};
            const nextDocuments = {
                legalContract: docs.legalContractName || "",
                anexo2: docs.anexo2Name || "anexo-2.pdf",
                anexo3: docs.anexo3Name || "anexo-3.pdf",
                financingCertificate: docs.financingCertificateName || "constancia-financiamiento.pdf",
            };
            setDocuments(nextDocuments);
            const nextCreditMeta = {
                ...(creditMeta || {}),
                linkNumber,
                dni,
                uploadName: creditMeta?.uploadName || localStorage.getItem("creditUploadName") || "",
                legalContract: nextDocuments.legalContract,
                anexo2: nextDocuments.anexo2,
                anexo3: nextDocuments.anexo3,
                financingCertificate: nextDocuments.financingCertificate,
            };
            setCreditMeta(nextCreditMeta);
            try {
                localStorage.setItem(CREDIT_FLOW_DOCUMENTS_KEY, JSON.stringify(nextDocuments));
                localStorage.setItem(CREDIT_FLOW_META_KEY, JSON.stringify(nextCreditMeta));
                localStorage.setItem(CREDIT_FLOW_FINISHED_KEY, "1");
                localStorage.setItem("completed", "1");
                localStorage.setItem(CREDIT_FLOW_STEP_KEY, "5");
            } catch (_) {}
            setSignatureVerified(true);
            setFlowMessage("Venta finalizada correctamente. Se generaron contrato, anexos y constancia de financiamiento.");
        } catch (err) {
            alert(err?.response?.data?.msg || err?.message || "No se pudo validar el SMS.");
        } finally {
            setSignatureLoading(false);
        }
    };

    const startNewCredit = () => {
        clearCreditFlowStorage();
        navigate("/evaluation-system", { replace: true });
    };

    const isFinalized = signatureVerified || localStorage.getItem(CREDIT_FLOW_FINISHED_KEY) === "1" || localStorage.getItem("completed") === "1";

    return (
        <div>
            <ScrollToTop />
            {loading && <PreloadFetch />}
            <Header user={user} />
            <section className="sectionBody ecFlowPage">
                <div className="containerCustom containerBody">
                    <div className="ecHero">
                        <div>
                            <p>MovilesPay</p>
                            <h1>Solicitar Crédito</h1>
                            <span>Flujo para evaluar, validar identidad, enrolar App de seguridad, firmar por SMS y generar documentos.</span>
                        </div>
                        <button className="ecGhost" onClick={goOutOfCreditFlow}>Volver</button>
                    </div>

                    <div className="ecSteps">
                        {STEPS.map((s) => <button key={s.id} className={step >= s.id ? "active" : ""} disabled={isFinalized} onClick={() => !isFinalized && step > s.id && setStep(s.id)}><b>{s.id}</b><span>{s.title}</span><small>{s.text}</small></button>)}
                    </div>

                    <div className="ecCard">
                        {step === 1 && <>
                            <h2>1. Selección y cotización</h2>
                            <div className="ecGrid2">
                                <label className="ecField">
                                    <span>Equipo / modelo</span>
                                    <ProfessionalSelect
                                        name="phoneName"
                                        options={phones}
                                        value={getOptionByValue(phones, form.phoneName)}
                                        onChange={selectPhone}
                                        placeholder={phones.length ? "Seleccione equipo" : "Sin equipos disponibles en esta sede"}
                                        isDisabled={!phones.length}
                                        formatOptionLabel={(option) => (
                                            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, width: "100%" }}>
                                                <span>{option.label}</span>
                                                {option.stockAvailable > 0 && (
                                                    <small style={{ whiteSpace: "nowrap", opacity: 0.72 }}>
                                                        {option.stockAvailable} disp.
                                                    </small>
                                                )}
                                            </div>
                                        )}
                                    />
                                    {phonesLoadError && <small className="ecValidationError">{phonesLoadError}</small>}
                                    {errors.phoneName && <small>{errors.phoneName}</small>}
                                </label>
                                <div>
                                    <Field name="phonePrice" label="Precio del equipo" value={form.phonePrice} error={errors.phonePrice} onUpdate={handleQuoteUpdate} validation="decimal" />
                                    <small className="ecInfoText">Máximo financiable: S/ 2,500.00.</small>
                                </div>
                                <Field name="creditRecord" label="Score / historial" max={4} value={form.creditRecord} error={errors.creditRecord} onUpdate={handleQuoteUpdate} validation="number" />
                                <label className="ecField"><span>Frecuencia</span><ProfessionalSelect name="paymentFrequency" options={frequencyOptions} value={getOptionByValue(frequencyOptions, form.paymentFrequency)} onChange={selectFrequency} placeholder="Seleccione frecuencia" />{errors.paymentFrequency && <small>{errors.paymentFrequency}</small>}</label>
                            </div>
                            <div className="ecInitials">
                                <h3>Inicial permitida</h3>
                                {initials.length ? initials.map((it) => <button key={it.pct} className={String(form.initialPercent) === String(it.pct) ? "active" : ""} onClick={() => selectInitial(it)}>{it.pct}%<span>S/ {it.price}</span></button>) : <p>Seleccione equipo y score para calcular iniciales.</p>}
                            </div>
                            <div className="ecPeriods">
                                <h3>Período</h3>
                                {periods.map((p, i) => <button key={p} className={form.paymentPeriod === i ? "active" : ""} onClick={() => update("paymentPeriod", i)}>{p}</button>)}
                            </div>
                            <div className="ecTotals">
                                <div><span>Monto a financiar</span><b>S/ {money(form.amountFinance)}</b></div>
                                <div><span>Cuota</span><b>S/ {money(form.fee)}</b></div>
                                <div><span>Total a pagar</span><b>S/ {money(form.totalAmountFinance)}</b></div>
                                <div><span>N° cuotas</span><b>{form.numFee || "-"}</b></div>
                            </div>
                        </>}

                        {step === 2 && <>
                            <h2>2. Identificación del cliente</h2>
                            <div className="ecGrid2">
                                <label className="ecField">
                                    <span>DNI</span>
                                    <input
                                        {...antiAutofillAttrs("dni")}
                                        value={form.dni || ""}
                                        placeholder="Ingrese DNI"
                                        inputMode="numeric"
                                        maxLength={8}
                                        onChange={handleDniChange}
                                    />
                                    {dniCreditValidation.loading && <small className="ecInfoText">Validando crédito activo en MovilesPay...</small>}
                                    {dniLookupLoading && !dniCreditValidation.loading && <small className="ecInfoText">Buscando nombres y apellidos...</small>}
                                    {dniCreditValidation.blocked && (
                                        <small className="ecValidationError">{dniCreditValidation.message || ACTIVE_CREDIT_MESSAGE}</small>
                                    )}
                                    {errors.dni && <small>{errors.dni}</small>}
                                </label>
                                <Field name="phoneNumber" label="Celular" max={9} value={form.phoneNumber} error={errors.phoneNumber} onUpdate={update} validation="phone" disabled={dniCreditValidation.blocked} />
                                <Field name="fullName" label="Nombres" value={form.fullName} error={errors.fullName} onUpdate={update} validation="fullName" readOnly={true} />
                                <Field name="lastName" label="Apellidos" value={form.lastName} error={errors.lastName} onUpdate={update} validation="lastName" readOnly={true} />
                                <Field name="email" label="Correo" type="email" value={form.email} error={errors.email} onUpdate={update} validation="email" disabled={dniCreditValidation.blocked} />
                            </div>
                            <div className="ecValidationPanel">
                                <div className="ecValidationInfo">
                                    <span className="ecValidationBadge">Validación móvil</span>
                                    <h3>Validación de identidad por QR</h3>
                                    <p>
                                        Genere el QR para que el cliente lo escanee con su celular. En la página móvil podrá tomar foto frontal y posterior del DNI, además de una verificación facial de 5 segundos.
                                    </p>
                                    <div className="ecValidationActions">
                                        <button className="ecPrimary" type="button" onClick={createClientValidationQr} disabled={validationLoading || dniCreditValidation.blocked || dniCreditValidation.loading}>
                                            {validationLoading ? "Procesando..." : validationQr.token ? "Regenerar QR" : "Generar QR de validación"}
                                        </button>
                                        {validationQr.token && (
                                            <button className="ecGhost" type="button" onClick={() => refreshClientValidationStatus(validationQr.token)} disabled={validationLoading}>
                                                Verificar estado
                                            </button>
                                        )}
                                    </div>
                                    {validationQr.message && <small className="ecInfoText">{validationQr.message}</small>}
                                    {errors.validation && <small className="ecValidationError">{errors.validation}</small>}
                                </div>

                                {validationQr.qrUrl && (
                                    <div className="ecQrValidationCard">
                                        <img src={validationQr.qrUrl} alt="QR de validación del cliente" />
                                        <strong>{validationQr.status === "completed" ? "Validación completada" : "Pendiente de validación"}</strong>
                                        {/* {validationQr.url && <small>{validationQr.url}</small>} */}
                                    </div>
                                )}
                            </div>
                        </>}

                        {step === 3 && <>
                            <h2>3. Perfil del cliente</h2>
                            <div className="ecGrid3" style={{ marginBottom: 15 }}>
                                <label className="ecField"><span>Tipo de vivienda</span><ProfessionalSelect name="livingPlace" options={livingPlaceOptions} value={getOptionByValue(livingPlaceOptions, form.livingPlace)} onChange={(o) => update("livingPlace", o?.value || "")} placeholder="Seleccione tipo de vivienda" /></label>
                                <label className="ecField"><span>Departamento</span><ProfessionalSelect name="department" options={departmentOptions} value={getOptionByValue(departmentOptions, form.department)} onChange={(o) => update("department", o?.value || "")} placeholder="Seleccione departamento" /></label>
                                <label className="ecField"><span>Ciudad</span><ProfessionalSelect name="city" options={cityOptions} value={getOptionByValue(cityOptions, form.city)} onChange={(o) => update("city", o?.value || "")} placeholder="Seleccione ciudad" /></label>
                            </div>
                            <div className="ecGrid2">
                                <Field name="location" label="Dirección" value={form.location} error={errors.location} onUpdate={update} validation="address" />
                                <Field name="locationNumber" label="Referencia / número" value={form.locationNumber} error={errors.locationNumber} onUpdate={update} validation="custom" />
                                <label className="ecField"><span>Tipo de trabajo</span><ProfessionalSelect name="jobType" options={jobTypeOptions} value={getOptionByValue(jobTypeOptions, form.jobType)} onChange={(o) => update("jobType", o?.value || "")} placeholder="Seleccione tipo de trabajo" /></label>
                                <Field name="jobName" label="Centro laboral / negocio" value={form.jobName} error={errors.jobName} onUpdate={update} validation="custom" />
                                <Field name="jobDirec" label="Dirección laboral" value={form.jobDirec} error={errors.jobDirec} onUpdate={update} validation="address" />
                                <label className="ecField"><span>Estado civil</span><ProfessionalSelect name="civilStatus" options={civilStatusOptions} value={getOptionByValue(civilStatusOptions, form.civilStatus)} onChange={(o) => update("civilStatus", o?.value || "")} placeholder="Seleccione estado civil" /></label>
                                <CreditCardRadio value={form.creditCard} error={errors.creditCard} onUpdate={update} />
                                <Field name="dependents" label="Dependientes" max={2} value={form.dependents} error={errors.dependents} onUpdate={update} validation="number" />
                                <Field name="monthlyIncome" label="Ingreso mensual" value={form.monthlyIncome} error={errors.monthlyIncome} onUpdate={update} validation="decimal" />
                                <Field name="monthlyExpenses" label="Gasto mensual" value={form.monthlyExpenses} error={errors.monthlyExpenses} onUpdate={update} validation="decimal" />
                            </div>
                        </>}

                        {step === 4 && <>
                            <h2>4. Aprobación preliminar</h2>
                            <div className="ecSummary">
                                <div><span>Cliente</span><b>{form.fullName} {form.lastName}</b><small>DNI {form.dni} · {form.phoneNumber}</small></div>
                                <div><span>Equipo</span><b>{form.phoneName}</b><small>Precio S/ {money(form.phonePrice)}</small></div>
                                <div><span>Financiamiento</span><b>S/ {money(form.amountFinance)}</b><small>{form.numFee} cuotas de S/ {money(form.fee)}</small></div>
                                <div><span>Total a pagar</span><b>S/ {money(form.totalAmountFinance)}</b><small>{form.paymentFrequency} · TCEA {form.tcea || "-"}</small></div>
                            </div>
                            <label className="ecCheck"><input type="checkbox" checked={tyc} onChange={(e) => setTyc(e.target.checked)} /> El cliente acepta términos y condiciones del crédito. Los documentos finales se generarán después del enrolamiento del App de seguridad y firma SMS.</label>
                            {errors.tyc && <p className="ecError">{errors.tyc}</p>}
                        </>}

                        {step === 5 && <>
                            <h2>5. Entrega, App de seguridad, firma SMS y documentos</h2>

                            <div className="ecDone ecEnrollBox">
                                <b>Crédito guardado correctamente.</b>
                                <p>
                                    Orden operativo: registre IMEI, genere y escanee el QR de App de seguridad en el celular nuevo,
                                    confirme la configuración del equipo, envíe el SMS de firma digital y finalmente genere contrato, anexos y constancia.
                                </p>
                            </div>

                            {flowMessage && <div className="ecNotice">{flowMessage}</div>}

                            <div className="ecSummary ecEnrollSummary">
                                <div><span>Cliente</span><b>{form.fullName} {form.lastName}</b><small>DNI {form.dni}</small></div>
                                <div><span>Equipo</span><b>{form.phoneName}</b><small>{form.phoneBrand} · {form.phoneModel}</small></div>
                                <div><span>Crédito</span><b>#{creditMeta?.linkNumber || localStorage.getItem("linkNumber") || "-"}</b><small>{form.paymentFrequency} · {form.numFee} cuotas</small></div>
                                <div><span>Total financiado</span><b>S/ {money(form.amountFinance)}</b><small>Total a pagar S/ {money(form.totalAmountFinance)}</small></div>
                            </div>

                            <div className="ecProcessPanel">
                                <div className={`ecProcessStep ${enroll.qrCode ? "done" : "active"}`}>
                                    <b>1</b>
                                    <div>
                                        <strong>Registrar IMEI y generar QR de App de seguridad</strong>
                                        <small>Este QR se debe escanear en el celular nuevo para activar el App de seguridad.</small>
                                    </div>
                                </div>
                                <div className={`ecProcessStep ${enroll.completed ? "done" : enroll.qrCode ? "active" : ""}`}>
                                    <b>2</b>
                                    <div>
                                        <strong>Completar enrolamiento del App de seguridad</strong>
                                        <small>Confirme cuando el enrolamiento del equipo haya terminado correctamente.</small>
                                    </div>
                                </div>
                                <div className={`ecProcessStep ${deviceConfigured ? "done" : enroll.completed ? "active" : ""}`}>
                                    <b>3</b>
                                    <div>
                                        <strong>Configurar celular nuevo</strong>
                                        <small>Solo después de configurar el equipo debe enviarse el SMS al cliente.</small>
                                    </div>
                                </div>
                                <div className={`ecProcessStep ${signatureVerified ? "done" : deviceConfigured ? "active" : ""}`}>
                                    <b>4</b>
                                    <div>
                                        <strong>Firma digital SMS y documentos</strong>
                                        <small>El SMS valida la firma y recién ahí se generan contrato, anexo 2, anexo 3 y constancia.</small>
                                    </div>
                                </div>
                            </div>

                            <div className="ecGrid2 ecEnrollGrid">
                                <label className="ecField">
                                    <span>DNI del cliente</span>
                                    <input {...antiAutofillAttrs("dniEnroll")} value={creditMeta?.dni || form.dni} readOnly />
                                </label>
                                <label className="ecField">
                                    <span>IMEI del equipo</span>
                                    <input
                                        {...antiAutofillAttrs("imeiEnroll")}
                                        value={enroll.imei}
                                        maxLength={15}
                                        placeholder="Ingrese 15 dígitos del IMEI"
                                        onChange={(e) => setEnroll((old) => ({ ...old, imei: onlyDigits(e.target.value, 15) }))}
                                        readOnly={enroll.completed || signatureVerified}
                                    />
                                </label>
                            </div>

                            <div className="ecActions ecEnrollActions">
                                {!enroll.qrCode && (
                                    <button className="ecPrimary" onClick={registerDevice} disabled={loading || signatureVerified}>
                                        Generar QR del App de seguridad
                                    </button>
                                )}
                            </div>

                            {enroll.message && <div className="ecNotice">{enroll.message}</div>}

                            {enroll.qrCode && !enroll.completed && (
                                <div className="ecQrPanel">
                                    <div>
                                        <h3>QR de enrolamiento del App de seguridad</h3>
                                        <p>Escanee este código en el celular nuevo y complete la activación antes de continuar.</p>
                                    </div>
                                    <img src={enroll.qrCode} alt="QR de enrolamiento del App de seguridad" />
                                </div>
                            )}

                            <div className="ecActions ecEnrollActions" style={{ justifyContent: "center" }}>
                                {enroll.qrCode && !enroll.completed && (
                                    <button
                                        className="ecPrimary ecPrimaryDark"
                                        onClick={completeEnroll}
                                        disabled={finalizingEnroll || loading}
                                    >
                                        {finalizingEnroll ? "Confirmando enrolamiento..." : "Confirmar enrolamiento del App de seguridad"}
                                    </button>
                                )}
                            </div>

                            {enroll.completed && !deviceConfigured && (
                                <div className="ecSignaturePanel">
                                    <h3>Configuración del celular nuevo</h3>
                                    <p>Antes de enviar el SMS, confirme que el equipo nuevo ya fue configurado y puede recibir mensajes.</p>
                                    <button className="ecPrimary" onClick={() => { setDeviceConfigured(true); setFlowMessage("Equipo configurado. Ya puede enviar el SMS de firma digital al cliente."); }}>
                                        Confirmar equipo configurado
                                    </button>
                                </div>
                            )}

                            {deviceConfigured && !signatureVerified && (
                                <div className="ecSignaturePanel">
                                    <h3>Firma digital por SMS</h3>
                                    <p>Se enviará el código al número del cliente: <b>{form.phoneNumber}</b>. Este código se imprimirá como firma digital en los documentos.</p>
                                    <div className="ecActions" style={{ justifyContent: "flex-start" }}>
                                        <button className="ecPrimary" onClick={sendSignatureSms} disabled={signatureLoading || signatureSmsSent}>
                                            {signatureSmsSent ? "SMS enviado" : "Enviar SMS de firma"}
                                        </button>
                                    </div>

                                    {signatureSmsSent && (
                                        <div className="ecGrid2" style={{ marginTop: 14 }}>
                                            <Field
                                                name="signatureCode"
                                                label="Código SMS"
                                                max={10}
                                                value={signatureCode}
                                                onUpdate={(_, value) => setSignatureCode(onlyDigits(value, 10))}
                                                validation="number"
                                            />
                                            <label className="ecField">
                                                <span>Acción</span>
                                                <button className="ecPrimary ecPrimaryDark" onClick={verifySignatureSms} disabled={signatureLoading}>
                                                    {signatureLoading ? "Validando y generando documentos..." : "Validar firma y generar documentos"}
                                                </button>
                                            </label>
                                        </div>
                                    )}
                                </div>
                            )}

                            {signatureVerified && (
                                <>
                                    <div className="ecDone ecEnrollBox">
                                        <b>✅ Venta finalizada correctamente.</b>
                                        <p>
                                            El crédito ya terminó operativamente. Se generaron correctamente el contrato,
                                            anexo 2, anexo 3 y la constancia de financiamiento.
                                        </p>
                                    </div>

                                    <div className="ecPdfPanel">
                                        <div className="ecPdfHeader">
                                            <div>
                                                <h3>Documentos finales generados</h3>
                                                <p>Revise, abra o imprima los documentos antes de finalizar la venta.</p>
                                            </div>
                                            <div className="ecPdfLinks">
                                                {creditMeta?.legalContract && <a href={buildUploadFileUrl(creditMeta.uploadName, creditMeta.legalContract)} target="_blank" rel="noreferrer">Abrir contrato</a>}
                                                {creditMeta?.anexo2 && <a href={buildUploadFileUrl(creditMeta.uploadName, creditMeta.anexo2)} target="_blank" rel="noreferrer">Abrir anexo 2</a>}
                                                {creditMeta?.anexo3 && <a href={buildUploadFileUrl(creditMeta.uploadName, creditMeta.anexo3)} target="_blank" rel="noreferrer">Abrir anexo 3</a>}
                                                {creditMeta?.financingCertificate && <a href={buildUploadFileUrl(creditMeta.uploadName, creditMeta.financingCertificate)} target="_blank" rel="noreferrer">Abrir constancia</a>}
                                            </div>
                                        </div>
                                        {creditMeta?.financingCertificate ? (
                                            <iframe title="Constancia de financiamiento" src={buildUploadFileUrl(creditMeta.uploadName, creditMeta.financingCertificate)} />
                                        ) : (
                                            <p>Documentos generados.</p>
                                        )}
                                    </div>

                                    <div className="ecActions" style={{ justifyContent: "center", marginTop: 18 }}>
                                        <button className="ecPrimary ecPrimaryDark" type="button" onClick={startNewCredit}>
                                            Finalizar venta
                                        </button>
                                    </div>
                                </>
                            )}
                        </>}
                        <div className="ecActions">
                            {step > 1 && step < 5 && <button className="ecGhost" onClick={() => setStep(step - 1)}>Atrás</button>}
                            {step < 4 && <button className="ecPrimary" onClick={next} disabled={step === 2 && (dniCreditValidation.blocked || dniCreditValidation.loading)}>Continuar</button>}
                            {step === 4 && <button className="ecPrimary" onClick={submitCredit}>Guardar crédito y continuar entrega</button>}
                        </div>
                    </div>
                </div>
            </section>
            <Footer />
        </div>
    );
}
